import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faComments, faPaperPlane, faPlus, faPen, faTrash, faCheck,
} from '@fortawesome/free-solid-svg-icons';

import { useAuthProvider } from '../providers/AuthProvider';
import { useTheme } from './themeControl/ThemeProvider';
import { useSupabase } from '../providers/SupabaseProvider';
import { useAlert } from '../providers/AlertProvider';
import { fetch_all_profiles } from '../api/profiles';
import {
    getConversations, getOrCreateConversation, getMessages,
    sendMessage, editMessage, deleteMessage, deleteConversation,
} from '../api/messages';
import logger from '../utils/logger';


// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTime(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    const now = new Date();
    const isToday =
        d.getDate() === now.getDate() &&
        d.getMonth() === now.getMonth() &&
        d.getFullYear() === now.getFullYear();
    return isToday
        ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : d.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
}


// ─── MessagesPage ─────────────────────────────────────────────────────────────

export default function MessagesPage() {
    const { profile } = useAuthProvider();
    const { theme } = useTheme();
    const supabase = useSupabase();
    const { showAlert } = useAlert();
    const myProfileId = profile?.id;

    // Conversations
    const [conversations, setConversations] = useState([]);
    const [selectedConvId, setSelectedConvId] = useState(null);

    // Messages
    const [messages, setMessages] = useState([]);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);

    // Compose
    const [newMessage, setNewMessage] = useState('');
    const [isSending, setIsSending] = useState(false);

    // Context menu (right-click own message)
    const [contextMenu, setContextMenu] = useState(null); // { x, y, message }

    // Inline edit
    const [editingId, setEditingId] = useState(null);
    const [editBody, setEditBody] = useState('');

    // Hovered conversation (for showing delete button)
    const [hoveredConvId, setHoveredConvId] = useState(null);

    // New conversation modal
    const [showNewConvModal, setShowNewConvModal] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [isCreatingConv, setIsCreatingConv] = useState(false);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // ── Styles ────────────────────────────────────────────────────────────────
    const isDark = theme === 'dark';
    const bg = isDark ? '#1a1a1a' : '#f0f2f5';
    const panelBg = isDark ? '#242424' : '#ffffff';
    const borderColor = isDark ? '#3a3a3a' : '#e0e0e0';
    const textColor = isDark ? '#eeeeee' : '#1a1a1a';
    const mutedColor = isDark ? '#888888' : '#888888';
    const sentBubble = '#1997eb';
    const recvBubble = isDark ? '#3a3a3a' : '#e9ecef';
    const recvText = textColor;

    // ── Data loaders ──────────────────────────────────────────────────────────

    const loadConversations = useCallback(async () => {
        try {
            const { status, data } = await getConversations();
            if (status === 200) setConversations(data?.data || []);
        } catch (err) {
            logger.error('loadConversations error:', err);
        }
    }, []);

    const loadMessages = useCallback(async (convId) => {
        if (!convId) return;
        try {
            const { status, data } = await getMessages(convId);
            if (status === 200) {
                setMessages(data?.data || []);
                // Refresh conversation list to update unread badges
                loadConversations();
            }
        } catch (err) {
            logger.error('loadMessages error:', err);
        }
    }, [loadConversations]);

    // ── Effects ───────────────────────────────────────────────────────────────

    // Refs to hold latest values inside realtime callbacks
    const selectedConvIdRef = useRef(selectedConvId);
    useEffect(() => { selectedConvIdRef.current = selectedConvId; }, [selectedConvId]);

    // Keep callback refs so the realtime handler never captures stale closures
    const loadConversationsRef = useRef(loadConversations);
    useEffect(() => { loadConversationsRef.current = loadConversations; }, [loadConversations]);
    const loadMessagesRef = useRef(loadMessages);
    useEffect(() => { loadMessagesRef.current = loadMessages; }, [loadMessages]);

    // Initial conversation load
    useEffect(() => {
        loadConversations();
    }, [loadConversations]);

    // Supabase Realtime — messages table
    // Empty dep array so the subscription is only created once; refs keep callbacks fresh
    useEffect(() => {
        if (!supabase) return;
        const channel = supabase
            .channel('messages-realtime-' + Date.now())
            .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, () => {
                // Always refresh conversation list (updates unread counts)
                loadConversationsRef.current();
                // Refresh open conversation's messages and auto-mark as read
                if (selectedConvIdRef.current) {
                    loadMessagesRef.current(selectedConvIdRef.current);
                }
            })
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [supabase]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load messages when conversation is selected
    useEffect(() => {
        setMessages([]);
        setEditingId(null);
        if (!selectedConvId) return;
        setIsLoadingMessages(true);
        loadMessages(selectedConvId).finally(() => setIsLoadingMessages(false));
    }, [selectedConvId, loadMessages]);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Dismiss context menu on any click
    useEffect(() => {
        const close = () => setContextMenu(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, []);

    // ── Handlers ─────────────────────────────────────────────────────────────

    const handleSend = async () => {
        const body = newMessage.trim();
        if (!body || !selectedConvId || isSending) return;
        setNewMessage('');
        setIsSending(true);
        try {
            const { status, data } = await sendMessage(selectedConvId, body);
            if (status === 200) {
                setMessages(prev => [...prev, data?.data]);
                loadConversations();
            }
        } catch (err) {
            logger.error('handleSend error:', err);
        } finally {
            setIsSending(false);
            inputRef.current?.focus();
        }
    };

    const handleEdit = async (messageId) => {
        const body = editBody.trim();
        if (!body) return;
        try {
            const { status, data } = await editMessage(messageId, body);
            if (status === 200) {
                setMessages(prev => prev.map(m => m.id === messageId ? data?.data : m));
                setEditingId(null);
            }
        } catch (err) {
            logger.error('handleEdit error:', err);
        }
    };

    const handleDelete = async (messageId) => {
        try {
            const { status } = await deleteMessage(messageId);
            if (status === 200) {
                setMessages(prev => prev.filter(m => m.id !== messageId));
                loadConversations();
            }
        } catch (err) {
            logger.error('handleDelete error:', err);
        }
    };

    const handleContextMenu = (e, message) => {
        if (message.from_profile_id !== myProfileId) return;
        e.preventDefault();
        setContextMenu({ x: e.clientX, y: e.clientY, message });
    };

    const openNewConvModal = async () => {
        if (staffList.length === 0) {
            try {
                const { status, data } = await fetch_all_profiles();
                if (status === 200) {
                    const all = Array.isArray(data) ? data : (data?.data || []);
                    const groupOrder = { 'DRIVER': 0, 'ADMIN': 1 };
                    const filtered = all.filter(p => p.id !== myProfileId && (p.group === 'DRIVER' || p.group === 'ADMIN'));
                    filtered.sort((a, b) => {
                        const gDiff = (groupOrder[a.group] ?? 99) - (groupOrder[b.group] ?? 99);
                        if (gDiff !== 0) return gDiff;
                        const nameDiff = (a.name || '').localeCompare(b.name || '');
                        if (nameDiff !== 0) return nameDiff;
                        return (a.surname || '').localeCompare(b.surname || '');
                    });
                    setStaffList(filtered);
                }
            } catch (err) {
                logger.error('openNewConvModal error:', err);
            }
        }
        setShowNewConvModal(true);
    };

    const handleNewConversation = async () => {
        if (!selectedUserId) return;
        setIsCreatingConv(true);
        try {
            const { status, data } = await getOrCreateConversation(selectedUserId);
            if (status === 200 || status === 201) {
                await loadConversations();
                setSelectedConvId(data?.data?.id);
                setShowNewConvModal(false);
                setSelectedUserId('');
            }
        } catch (err) {
            logger.error('handleNewConversation error:', err);
        } finally {
            setIsCreatingConv(false);
        }
    };

    const handleDeleteConversation = (convId, otherName) => {
        showAlert({
            title: 'Delete Conversation',
            message: `Delete your conversation with ${otherName}? You won't see past messages, but new ones will still appear.`,
            type: 'danger',
            buttons: [
                {
                    label: 'Delete',
                    type: 'danger',
                    onClick: async () => {
                        const { status } = await deleteConversation(convId);
                        if (status === 200) {
                            if (selectedConvId === convId) setSelectedConvId(null);
                            loadConversations();
                        }
                    },
                },
                { label: 'Cancel', type: 'secondary' },
            ],
        });
    };

    const selectedConv = conversations.find(c => c.id === selectedConvId);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <>
        <style>{`
            .msg-modal-overlay { background: transparent !important; z-index: 1055 !important; }
            .msg-modal-content { display: flex !important; flex-direction: column !important; width: 100% !important; height: auto !important; padding: 0 !important; }
        `}</style>
        <div style={{
            display: 'flex',
            height: 'calc(100vh - 120px)',
            marginTop: '120px',
            background: bg,
            color: textColor,
            overflow: 'hidden',
            fontFamily: 'inherit',
        }}>

            {/* ── Left panel: conversation list ── */}
            <div style={{
                width: '300px',
                borderRight: `1px solid ${borderColor}`,
                display: 'flex',
                flexDirection: 'column',
                background: panelBg,
                flexShrink: 0,
            }}>
                {/* Header */}
                <div style={{
                    padding: '16px',
                    borderBottom: `1px solid ${borderColor}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <h5 style={{ margin: 0, fontSize: '16px' }}>
                        <FontAwesomeIcon icon={faComments} style={{ width: '16px', marginRight: '8px' }} />
                        Messages
                    </h5>
                    <Button size="sm" variant="primary" onClick={openNewConvModal} title="New conversation">
                        <FontAwesomeIcon icon={faPlus} style={{ width: '14px' }} />
                    </Button>
                </div>

                {/* Conversation list */}
                <div style={{ overflowY: 'auto', flex: 1 }}>
                    {conversations.length === 0 ? (
                        <p style={{ padding: '20px 16px', color: mutedColor, fontSize: '13px', textAlign: 'center' }}>
                            No conversations yet.<br />Start one with the + button.
                        </p>
                    ) : (
                        [...conversations].sort((a, b) => {
                                const aUnread = a.unread_count > 0 ? 0 : 1;
                                const bUnread = b.unread_count > 0 ? 0 : 1;
                                if (aUnread !== bUnread) return aUnread - bUnread;
                                const aName = `${a.other_participant?.name || ''} ${a.other_participant?.surname || ''}`.toLowerCase();
                                const bName = `${b.other_participant?.name || ''} ${b.other_participant?.surname || ''}`.toLowerCase();
                                return aName.localeCompare(bName);
                            }).map(conv => {
                            const isSelected = conv.id === selectedConvId;
                            const other = conv.other_participant;
                            const initials = `${other.name?.[0] || ''}${other.surname?.[0] || ''}`.toUpperCase();

                            return (
                                <div
                                    key={conv.id}
                                    onClick={() => setSelectedConvId(conv.id)}
                                    onMouseEnter={() => setHoveredConvId(conv.id)}
                                    onMouseLeave={() => setHoveredConvId(null)}
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        background: isSelected
                                            ? (isDark ? '#2e3d4f' : '#e3f2fd')
                                            : 'transparent',
                                        borderBottom: `1px solid ${borderColor}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        transition: 'background 0.15s',
                                        position: 'relative',
                                    }}
                                >
                                    {hoveredConvId === conv.id && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteConversation(conv.id, `${other.name} ${other.surname}`.trim());
                                            }}
                                            title="Delete conversation"
                                            style={{
                                                position: 'absolute',
                                                bottom: '6px',
                                                right: '8px',
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#e53935',
                                                padding: '2px 4px',
                                                borderRadius: '4px',
                                                zIndex: 1,
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTrash} style={{ width: '12px' }} />
                                        </button>
                                    )}
                                    {/* Avatar */}
                                    <div style={{
                                        width: '42px',
                                        height: '42px',
                                        borderRadius: '50%',
                                        background: '#1997eb',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        flexShrink: 0,
                                    }}>
                                        {initials || '?'}
                                    </div>

                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '2px',
                                        }}>
                                            <span style={{
                                                fontWeight: conv.unread_count > 0 ? '700' : '500',
                                                fontSize: '14px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '140px',
                                            }}>
                                                {other.name} {other.surname}
                                            </span>
                                            <span style={{ fontSize: '11px', color: mutedColor, flexShrink: 0 }}>
                                                {formatTime(conv.last_message_at)}
                                            </span>
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}>
                                            <span style={{
                                                fontSize: '12px',
                                                color: mutedColor,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '180px',
                                            }}>
                                                {conv.last_message
                                                    ? (conv.last_message.from_profile_id === myProfileId ? 'You: ' : '')
                                                        + conv.last_message.body
                                                    : 'No messages yet'}
                                            </span>
                                            {conv.unread_count > 0 && (
                                                <span style={{
                                                    background: '#e53935',
                                                    color: '#fff',
                                                    borderRadius: '50%',
                                                    width: '20px',
                                                    height: '20px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '13px',
                                                    fontWeight: '900',
                                                    flexShrink: 0,
                                                }}>
                                                    !
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* ── Right panel: chat window ── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

                {!selectedConvId ? (
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: mutedColor,
                        flexDirection: 'column',
                        gap: '12px',
                    }}>
                        <FontAwesomeIcon icon={faComments} style={{ width: '60px', height: '60px', opacity: 0.2 }} />
                        <p style={{ margin: 0, fontSize: '15px' }}>Select a conversation to start messaging</p>
                    </div>
                ) : (
                    <>
                        {/* Chat header */}
                        <div style={{
                            padding: '14px 20px',
                            borderBottom: `1px solid ${borderColor}`,
                            background: panelBg,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                        }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: '#1997eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontWeight: 'bold',
                                fontSize: '13px',
                                flexShrink: 0,
                            }}>
                                {`${selectedConv?.other_participant?.name?.[0] || ''}${selectedConv?.other_participant?.surname?.[0] || ''}`.toUpperCase()}
                            </div>
                            <div>
                                <div style={{ fontWeight: '600', fontSize: '15px' }}>
                                    {selectedConv?.other_participant?.name} {selectedConv?.other_participant?.surname}
                                </div>
                                <div style={{ fontSize: '11px', color: mutedColor }}>
                                    {selectedConv?.other_participant?.group}
                                </div>
                            </div>
                        </div>

                        {/* Messages area */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '16px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                        }}>
                            {isLoadingMessages ? (
                                <div style={{ textAlign: 'center', color: mutedColor, marginTop: '30px' }}>
                                    <Spinner size="sm" /> Loading messages...
                                </div>
                            ) : messages.length === 0 ? (
                                <p style={{ textAlign: 'center', color: mutedColor, marginTop: '30px', fontSize: '14px' }}>
                                    No messages yet. Say hello!
                                </p>
                            ) : (
                                messages.map(msg => {
                                    const isMine = msg.from_profile_id === myProfileId;
                                    const isEditing = editingId === msg.id;

                                    return (
                                        <div
                                            key={msg.id}
                                            style={{
                                                display: 'flex',
                                                justifyContent: isMine ? 'flex-end' : 'flex-start',
                                                marginBottom: '2px',
                                                alignItems: 'flex-end',
                                                gap: '4px',
                                            }}
                                        >
                                            {isMine && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleContextMenu(e, msg); }}
                                                    title="Edit / Delete"
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        color: mutedColor,
                                                        fontSize: '18px',
                                                        lineHeight: 1,
                                                        padding: '0 2px',
                                                        flexShrink: 0,
                                                        alignSelf: 'center',
                                                    }}
                                                >
                                                    ⋯
                                                </button>
                                            )}
                                            <div
                                                onContextMenu={(e) => handleContextMenu(e, msg)}
                                                style={{
                                                    maxWidth: '65%',
                                                    padding: '10px 14px',
                                                    borderRadius: isMine
                                                        ? '18px 18px 4px 18px'
                                                        : '18px 18px 18px 4px',
                                                    background: isMine ? sentBubble : recvBubble,
                                                    color: isMine ? '#ffffff' : recvText,
                                                    cursor: isMine ? 'context-menu' : 'default',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                }}
                                            >
                                                {isEditing ? (
                                                    <div>
                                                        <Form.Control
                                                            as="textarea"
                                                            value={editBody}
                                                            onChange={e => setEditBody(e.target.value)}
                                                            rows={2}
                                                            autoFocus
                                                            style={{ fontSize: '13px', marginBottom: '8px', resize: 'none' }}
                                                        />
                                                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                                            <Button
                                                                size="sm"
                                                                variant="success"
                                                                onClick={() => handleEdit(msg.id)}
                                                                style={{ fontWeight: '600', minWidth: '64px' }}
                                                            >
                                                                Save
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="danger"
                                                                onClick={() => setEditingId(null)}
                                                                style={{ fontWeight: '600', minWidth: '64px' }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div style={{
                                                            fontSize: '16px',
                                                            lineHeight: '1.5',
                                                            wordBreak: 'break-word',
                                                            whiteSpace: 'pre-wrap',
                                                        }}>
                                                            {msg.body}
                                                        </div>

                                                        {/* Timestamp + edited + read receipt */}
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-end',
                                                            gap: '4px',
                                                            marginTop: '4px',
                                                        }}>
                                                            {msg.is_edited && (
                                                                <span style={{
                                                                    fontSize: '10px',
                                                                    opacity: 0.65,
                                                                    fontStyle: 'italic',
                                                                }}>
                                                                    edited
                                                                </span>
                                                            )}
                                                            <span style={{ fontSize: '10px', opacity: 0.65 }}>
                                                                {formatTime(msg.created_at)}
                                                            </span>
                                                            {/* Double-tick read receipt on sent messages */}
                                                            {isMine && (
                                                                <span style={{
                                                                    fontSize: '11px',
                                                                    color: msg.is_read ? '#00e676' : '#ef5350',
                                                                    background: 'rgba(0,0,0,0.25)',
                                                                    borderRadius: '50px',
                                                                    padding: '2px 5px',
                                                                    lineHeight: 1,
                                                                }}>
                                                                    <FontAwesomeIcon icon={faCheck} style={{ width: '9px' }} />
                                                                    <FontAwesomeIcon
                                                                        icon={faCheck}
                                                                        style={{ width: '9px', marginLeft: '-4px' }}
                                                                    />
                                                                </span>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input area */}
                        <div style={{
                            padding: '12px 96px 12px 20px',
                            borderTop: `1px solid ${borderColor}`,
                            background: panelBg,
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'flex-end',
                        }}>
                            <Form.Control
                                ref={inputRef}
                                as="textarea"
                                rows={1}
                                value={newMessage}
                                onChange={e => setNewMessage(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                                placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
                                style={{ resize: 'none', fontSize: '14px' }}
                            />
                            <Button
                                variant="primary"
                                onClick={handleSend}
                                disabled={isSending || !newMessage.trim()}
                                style={{ flexShrink: 0, height: '38px' }}
                                title="Send"
                            >
                                {isSending
                                    ? <Spinner size="sm" />
                                    : <FontAwesomeIcon icon={faPaperPlane} style={{ width: '16px' }} />
                                }
                            </Button>
                        </div>
                    </>
                )}
            </div>

            {/* ── Context menu ── */}
            {contextMenu && (
                <div
                    onClick={e => e.stopPropagation()}
                    style={{
                        position: 'fixed',
                        top: contextMenu.y,
                        left: contextMenu.x,
                        background: panelBg,
                        border: `1px solid ${borderColor}`,
                        borderRadius: '8px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                        zIndex: 9999,
                        padding: '4px 0',
                        minWidth: '130px',
                    }}
                >
                    <div
                        style={{
                            padding: '9px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                        onClick={() => {
                            setEditingId(contextMenu.message.id);
                            setEditBody(contextMenu.message.body);
                            setContextMenu(null);
                        }}
                    >
                        <FontAwesomeIcon icon={faPen} style={{ width: '14px' }} /> Edit
                    </div>
                    <div
                        style={{
                            padding: '9px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#e53935',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                        onClick={() => {
                            handleDelete(contextMenu.message.id);
                            setContextMenu(null);
                        }}
                    >
                        <FontAwesomeIcon icon={faTrash} style={{ width: '14px' }} /> Delete
                    </div>
                </div>
            )}

            {/* ── New conversation modal ── */}
            <Modal show={showNewConvModal} onHide={() => { setShowNewConvModal(false); setSelectedUserId(''); }} centered className="msg-modal-overlay" contentClassName="msg-modal-content">
                <Modal.Header
                    closeButton
                    style={{ background: panelBg, color: textColor, borderColor }}
                >
                    <Modal.Title style={{ fontSize: '16px' }}>New Conversation</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ background: panelBg, color: textColor }}>
                    <Form.Group>
                        <Form.Label style={{ fontSize: '14px' }}>Select a person to message</Form.Label>
                        <Form.Select
                            value={selectedUserId}
                            onChange={e => setSelectedUserId(e.target.value)}
                            style={{ fontSize: '14px' }}
                        >
                            <option value="">-- Choose a user --</option>
                            {staffList.map(s => (
                                <option key={s.id} value={s.id}>
                                    {s.name} {s.surname} ({s.group})
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer style={{ background: panelBg, borderColor }}>
                    <Button variant="secondary" onClick={() => { setShowNewConvModal(false); setSelectedUserId(''); }}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        disabled={!selectedUserId || isCreatingConv}
                        onClick={handleNewConversation}
                    >
                        {isCreatingConv ? <><Spinner size="sm" /> Opening…</> : 'Open Chat'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
        </>
    );
}
